import { useCallback } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useSnackbar } from 'notistack';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';

import {
  ExistingDraftResponse,
  cancellationApiService,
  CancellationOrderResponseDto,
  CreateCancellationOrderPayload,
  UpdateCancellationOrderPayload,
  CreateCancellationOrderResponse,
} from '@/services/cancellation.service';

interface UseCancellationOrdersOptions {
  organizationId?: string;
  orderId?: string;
  cancellationId?: string;
  onSuccess?: (data: CancellationOrderResponseDto) => void;
  onError?: (error: Error) => void;
}

export function useCancellationOrders(options: UseCancellationOrdersOptions) {
  const { organizationId, orderId, cancellationId, onSuccess, onError } = options ?? {};
  const { enqueueSnackbar } = useSnackbar();
  const { t } = useTranslation();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const {
    data: cancellationOrders,
    isLoading: isLoadingOrders,
    error: ordersError,
    refetch: refetchOrders,
  } = useQuery({
    queryKey: ['cancellation-orders', organizationId],
    queryFn: () => cancellationApiService.getCancellationOrders(organizationId!),
    enabled: !!organizationId,
    staleTime: 5 * 60 * 1000,
  });

  const {
    data: cancellationOrderDetail,
    isLoading: isLoadingDetail,
    error: detailError,
    refetch: refetchDetail,
  } = useQuery({
    queryKey: ['cancellation-order', cancellationId],
    queryFn: () => cancellationApiService.getCancellationOrderById(cancellationId!),
    enabled: !!cancellationId,
  });

  const {
    data: cancellableItems,
    isLoading: isLoadingCancellableItems,
    error: cancellableItemsError,
    refetch: refetchCancellableItems,
  } = useQuery({
    queryKey: ['cancellation-items', orderId],
    queryFn: () => cancellationApiService.getItemsForCancellation(orderId!),
    enabled: !!orderId,
    staleTime: 2 * 60 * 1000,
  });

  const createCancellationMutation = useMutation({
    mutationFn: (payload: CreateCancellationOrderPayload) =>
      cancellationApiService.createCancellationOrder(payload),
    onSuccess: (data: CreateCancellationOrderResponse) => {
      // Check if the response indicates an existing draft
      if (data?.status === 'error') {
        const existingDraftData = data as ExistingDraftResponse;
        enqueueSnackbar(
          t(
            existingDraftData.message ||
              'A draft cancellation request already exists for this order. Edit or finalize the existing draft before creating a new one.'
          ),
          {
            variant: 'warning',
            autoHideDuration: 8000,
          }
        );
        return;
      }

      // Normal success case - data is CancellationOrderResponseDto
      const orderData = data as CancellationOrderResponseDto;
      enqueueSnackbar(t('Cancellation order created successfully'), { variant: 'success' });
      queryClient.invalidateQueries({ queryKey: ['cancellation-orders'] });

      onSuccess?.(orderData);
      if (organizationId) {
        navigate(
          `/management/accounts/organizations/${organizationId}/cancellation-orders/${orderData._id}`
        );
      }
    },
    onError: (error: any) => {
      enqueueSnackbar(t('Error creating cancellation order'), { variant: 'error' });
      onError?.(error as Error);
    },
  });

  const updateCancellationMutation = useMutation({
    mutationFn: (payload: { id: string; data: UpdateCancellationOrderPayload }) =>
      cancellationApiService.updateCancellationOrder(payload.id, payload.data),
    onSuccess: (data) => {
      enqueueSnackbar(t('Cancellation order updated successfully'), { variant: 'success' });
      queryClient.invalidateQueries({ queryKey: ['cancellation-order', cancellationId] });
      queryClient.invalidateQueries({ queryKey: ['cancellation-orders'] });

      onSuccess?.(data);
    },
    onError: (error) => {
      enqueueSnackbar(t('Error updating cancellation order'), { variant: 'error' });
      onError?.(error as Error);
    },
  });

  const deleteCancellationMutation = useMutation({
    mutationFn: (id: string) => cancellationApiService.deleteCancellationOrder(id),
    onSuccess: () => {
      enqueueSnackbar(t('Cancellation order deleted successfully'), { variant: 'success' });
      if (orderId) {
        queryClient.invalidateQueries(['cancellations-by-order', orderId]);
      } else {
        queryClient.invalidateQueries({ queryKey: ['cancellation-orders'] });
        navigateToOrdersList();
      }
    },
    onError: (error) => {
      enqueueSnackbar(t('Error deleting cancellation order'), { variant: 'error' });
      onError?.(error as Error);
    },
  });

  const confirmCancellationMutation = useMutation({
    mutationFn: (id: string) => {
      return cancellationApiService.confirmCancellationOrder(id);
    },
    onSuccess: (data) => {
      enqueueSnackbar(t('Cancellation order has been confirmed successfully'), {
        variant: 'success',
        autoHideDuration: 5000,
      });
      queryClient.invalidateQueries({ queryKey: ['cancellation-order', cancellationId] });
      queryClient.invalidateQueries({ queryKey: ['cancellation-orders'] });
      if (organizationId) {
        queryClient.invalidateQueries({ queryKey: ['cancellation-orders', organizationId] });
      }

      onSuccess?.(data);
      navigateToOrdersList();
    },
    onError: (error) => {
      enqueueSnackbar(t('Error confirming cancellation order'), { variant: 'error' });
      onError?.(error as Error);
    },
  });

  const createCancellation = useCallback(
    (payload: CreateCancellationOrderPayload) => {
      return createCancellationMutation.mutateAsync(payload);
    },
    [createCancellationMutation]
  );

  const updateCancellation = useCallback(
    (id: string, payload: UpdateCancellationOrderPayload) => {
      return updateCancellationMutation.mutateAsync({ id, data: payload });
    },
    [updateCancellationMutation]
  );

  const deleteCancellation = useCallback(
    (id: string) => {
      return deleteCancellationMutation.mutateAsync(id);
    },
    [deleteCancellationMutation]
  );

  const confirmCancellation = useCallback(
    (id: string) => {
      return confirmCancellationMutation.mutateAsync(id);
    },
    [confirmCancellationMutation]
  );

  const navigateToCancellationDetail = useCallback(
    (cancellationOrderId: string) => {
      if (organizationId) {
        navigate(
          `/management/accounts/organizations/${organizationId}/cancellation-orders/${cancellationOrderId}`
        );
      } else {
        navigate(`/management/accounts/cancellation-orders/${cancellationOrderId}`);
      }
    },
    [navigate, organizationId]
  );

  const navigateToOrdersList = useCallback(() => {
    if (organizationId) {
      navigate(`/management/accounts/organizations/${organizationId}/cancellation-orders`);
    } else {
      navigate('/management/accounts/cancellation-orders');
    }
  }, [navigate, organizationId]);

  return {
    // Data
    cancellationOrders,
    cancellationOrderDetail,
    cancellableItems,

    // Loading states
    isLoadingOrders,
    isLoadingDetail,
    isLoadingCancellableItems,
    isCreating: createCancellationMutation.isLoading,
    isUpdating: updateCancellationMutation.isLoading,
    isDeleting: deleteCancellationMutation.isLoading,
    isConfirming: confirmCancellationMutation.isLoading,

    // Error states
    ordersError,
    detailError,
    cancellableItemsError,
    createError: createCancellationMutation.error,
    updateError: updateCancellationMutation.error,
    deleteError: deleteCancellationMutation.error,
    confirmError: confirmCancellationMutation.error,

    // Actions
    createCancellation,
    updateCancellation,
    deleteCancellation,
    confirmCancellation,
    refetchOrders,
    refetchDetail,
    refetchCancellableItems,

    // Navigation helpers
    navigateToCancellationDetail,
    navigateToOrdersList,

    // Raw mutations
    createCancellationMutation,
    updateCancellationMutation,
    deleteCancellationMutation,
    confirmCancellationMutation,
  };
}

export default useCancellationOrders;
